import { useEffect, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { ApiError } from '../../api/index.js';

const REASON_LABELS = {
  unspecified:          'Unspecified',
  keyCompromise:        'Key compromise',
  caCompromise:         'CA compromise',
  affiliationChanged:   'Affiliation changed',
  superseded:           'Superseded',
  cessationOfOperation: 'Cessation of operation',
  certificateHold:      'Certificate hold',
  privilegeWithdrawn:   'Privilege withdrawn',
};

export function RevokeCertModal({ open, certificate, reasons = [], onClose, onConfirm }) {
  const [reason, setReason] = useState('keyCompromise');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setReason(reasons[0] || 'keyCompromise');
    setBusy(false);
    setError(null);
  }, [open, reasons]);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Revocation failed.');
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={`Revoke certificate — ${certificate?.subjectCn || ''}`}
      description="Revocation is final. The certificate will appear in the next CRL."
      size="sm"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary" data-skip-autofocus>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !reason}
            className="btn bg-rose-600 text-white hover:bg-rose-700 disabled:bg-rose-400"
          >
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Revoking…
              </>
            ) : (
              <>
                <i className="fa-solid fa-ban" aria-hidden="true" /> Revoke
              </>
            )}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}
        {certificate?.serial && (
          <div className="rounded-2xl border border-line bg-canvas px-4 py-3 text-xs text-mist">
            <div className="font-mono break-all">Serial: {certificate.serial}</div>
            {certificate.fingerprintSha256 && (
              <div className="mt-1 font-mono break-all">SHA-256: {certificate.fingerprintSha256}</div>
            )}
          </div>
        )}
        <div>
          <label htmlFor="reason" className="field-label">Revocation reason (RFC 5280)</label>
          <select
            id="reason"
            className="field"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
          >
            {reasons.map((r) => (
              <option key={r} value={r}>
                {REASON_LABELS[r] || r}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Modal>
  );
}
